import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  Truck,
  Plus,
  Trash2,
  FileSpreadsheet,
  Loader2,
  Sparkles,
  Lock,
  ChevronRight,
  Tag,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { suppliersApi } from '../lib/suppliersApi';
import { billingApi } from '../lib/billingApi';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { isStoreApp } from '../lib/platform';

const fmt = (str, vars) =>
  Object.entries(vars || {}).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), v),
    str
  );

// Cache key for offline support — last successful /suppliers list per user.
const suppliersCacheKey = (uid) => `sp.suppliers.list.cache:${uid || 'anon'}`;

function readSuppliersCache(uid) {
  try {
    const raw = localStorage.getItem(suppliersCacheKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeSuppliersCache(uid, data) {
  try { localStorage.setItem(suppliersCacheKey(uid), JSON.stringify(data)); }
  catch { /* quota / disabled — ignore */ }
}

export default function Suppliers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useT();
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [isOffline, setIsOffline] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // 🌐 Offline-first: charger directement le cache si pas de réseau.
    if (!navigator.onLine) {
      const cached = readSuppliersCache(user?.id);
      if (cached) setSuppliers(cached);
      setPaywall(false);
      setIsOffline(true);
      setLoading(false);
      return;
    }
    try {
      const data = await suppliersApi.list();
      const arr = data.suppliers || [];
      setSuppliers(arr);
      setPaywall(false);
      setIsOffline(false);
      writeSuppliersCache(user?.id, arr);
    } catch (err) {
      if (err.status === 402) {
        setPaywall(true);
      } else {
        // Fallback offline: utiliser le cache local plutôt qu'un toast d'erreur.
        const cached = readSuppliersCache(user?.id);
        if (cached) {
          setSuppliers(cached);
          setIsOffline(true);
        } else {
          toast.error(err.message || t('supp.load_error'));
        }
      }
    } finally {
      setLoading(false);
    }
  }, [t, user]);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error(t('supp.name_required'));
      return;
    }
    if (!navigator.onLine) {
      toast.error(lang === 'fr'
        ? 'Hors ligne — cette action nécessite une connexion internet.'
        : 'Offline — this action requires an internet connection.');
      return;
    }
    setCreating(true);
    try {
      const sup = await suppliersApi.create({
        name: newName.trim(),
        category: newCategory.trim() || null,
      });
      setSuppliers((arr) => [sup, ...arr]);
      setNewName('');
      setNewCategory('');
      setShowAdd(false);
      toast.success(t('supp.added'));
      navigate(`/suppliers/${sup.id}`);
    } catch (err) {
      toast.error(err.message || t('supp.add_failed'));
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(fmt(t('supp.delete_confirm'), { name: s.name }))) return;
    if (!navigator.onLine) {
      toast.error(lang === 'fr'
        ? 'Hors ligne — cette action nécessite une connexion internet.'
        : 'Offline — this action requires an internet connection.');
      return;
    }
    try {
      await suppliersApi.remove(s.id);
      setSuppliers((arr) => arr.filter((x) => x.id !== s.id));
      toast.success(t('supp.deleted'));
    } catch (err) {
      toast.error(err.message || t('supp.delete_failed'));
    }
  };

  const handleUpgrade = async () => {
    try {
      const status = await billingApi.status();
      if (status?.status === 'active' || status?.status === 'trialing') {
        toast.info(t('supp.already_active'));
        load();
        return;
      }
    } catch {
      // ignore — just navigate to pricing
    }
    navigate('/pricing');
  };

  if (loading) {
    return (
      <Layout title={t('supp.title')}>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7A1F2B' }} />
        </div>
      </Layout>
    );
  }

  if (paywall) {
    return (
      <Layout title={t('supp.title')}>
        <div className="max-w-2xl mx-auto py-6">
          <div
            className="rounded-2xl p-8 text-center border-2"
            style={{ borderColor: '#C9A961', backgroundColor: '#FFF' }}
            data-testid="suppliers-paywall"
          >
            <div
              className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
              style={{ backgroundColor: '#7A1F2B', color: '#C9A961' }}
            >
              <Lock className="w-8 h-8" />
            </div>
            <div
              className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest mb-2"
              style={{ color: '#C9A961' }}
            >
              <Sparkles className="w-3 h-3" />
              {t('supp.pro_badge')}
            </div>
            <h1 className="font-heading text-3xl font-black tracking-tighter mb-3" style={{ color: '#5A1620' }}>
              {t('supp.paywall_title')}
            </h1>
            <p
              className="text-base mb-6 leading-relaxed"
              style={{ color: '#4A5568' }}
              dangerouslySetInnerHTML={{ __html: t('supp.paywall_desc_html') }}
            />
            {isStoreApp() ? (
              <div
                className="text-sm px-3 py-3 rounded-lg"
                style={{ backgroundColor: '#FAF7F2', color: '#6B7280', border: '1px solid #E5DCC9' }}
                data-testid="suppliers-paywall-store-notice"
              >
                {lang === 'fr'
                  ? "Cette fonctionnalité requiert un abonnement non disponible depuis cette app."
                  : 'This feature requires a subscription that is not available from this app.'}
              </div>
            ) : (
              <button
                onClick={handleUpgrade}
                className="w-full py-3.5 rounded-xl font-semibold text-white text-base"
                style={{ backgroundColor: '#7A1F2B' }}
                data-testid="suppliers-upgrade-btn"
              >
                {t('supp.paywall_btn')}
              </button>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t('supp.title')}>
      {isOffline && (
        <div
          className="mb-4 p-3 rounded-xl flex items-center gap-2 text-sm"
          style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E' }}
          data-testid="suppliers-offline-banner"
        >
          <WifiOff className="w-4 h-4 flex-shrink-0" />
          <span>{t('supp.offline_banner') || (t('common.offline_cached') || 'Hors ligne — données en cache. Les modifications nécessitent une connexion.')}</span>
        </div>
      )}

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter mb-1" style={{ color: '#5A1620' }}>
            {t('supp.title')}
          </h1>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {t('supp.subtitle')}
          </p>
        </div>
        {!showAdd && !isOffline && (
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2.5 rounded-xl font-semibold text-white text-sm inline-flex items-center gap-2"
            style={{ backgroundColor: '#7A1F2B' }}
            data-testid="add-supplier-btn"
          >
            <Plus size={16} /> {t('supp.add')}
          </button>
        )}
      </div>

      {showAdd && (
        <form
          onSubmit={onCreate}
          className="card p-5 mb-5 space-y-3"
          data-testid="add-supplier-form"
        >
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#5A1620' }}>
              {t('supp.name_label')}
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('supp.name_placeholder')}
              className="w-full px-4 py-2.5 rounded-lg border-2 outline-none"
              style={{ borderColor: '#E5DCC9' }}
              autoFocus
              data-testid="supplier-name-input"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#5A1620' }}>
              {t('supp.category_label')}
            </label>
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder={t('supp.category_placeholder')}
              className="w-full px-4 py-2.5 rounded-lg border-2 outline-none"
              style={{ borderColor: '#E5DCC9' }}
              data-testid="supplier-category-input"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 py-2.5 rounded-xl font-semibold text-white text-sm inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: '#7A1F2B' }}
              data-testid="supplier-create-submit"
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {t('supp.create')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                setNewName('');
                setNewCategory('');
              }}
              className="px-4 py-2.5 rounded-xl font-semibold text-sm"
              style={{ backgroundColor: '#F5EFE0', color: '#5A1620' }}
              data-testid="supplier-create-cancel"
            >
              {t('supp.cancel')}
            </button>
          </div>
        </form>
      )}

      {suppliers.length === 0 ? (
        <div className="card p-8 text-center" data-testid="empty-suppliers">
          <div
            className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center"
            style={{ backgroundColor: '#FAF7F2', color: '#7A1F2B' }}
          >
            <Truck className="w-7 h-7" />
          </div>
          <h2 className="font-heading text-xl font-black mb-2" style={{ color: '#5A1620' }}>
            {t('supp.empty_title')}
          </h2>
          <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
            {t('supp.empty_desc')}
          </p>
          {!showAdd && (
            <button
              onClick={() => setShowAdd(true)}
              className="px-5 py-2.5 rounded-xl font-semibold text-white text-sm inline-flex items-center gap-2"
              style={{ backgroundColor: '#7A1F2B' }}
              data-testid="empty-add-supplier-btn"
            >
              <Plus size={16} /> {t('supp.empty_btn')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {suppliers.map((s) => (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/suppliers/${s.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/suppliers/${s.id}`);
                }
              }}
              className="card p-4 w-full text-left hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#7A1F2B] focus:ring-offset-2"
              data-testid={`supplier-row-${s.id}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: '#FAF7F2', color: '#7A1F2B' }}
                >
                  <Truck className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-base truncate" style={{ color: '#5A1620' }}>
                    {s.name}
                  </div>
                  {s.category && (
                    <div className="text-xs mt-0.5 inline-flex items-center gap-1" style={{ color: '#6B7280' }}>
                      <Tag size={11} /> {s.category}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(s);
                    }}
                    className="p-2 text-red-700 hover:bg-red-50 rounded-lg"
                    data-testid={`supplier-delete-${s.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                  <ChevronRight size={18} style={{ color: '#9CA3AF' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {suppliers.length > 0 && (
        <div
          className="mt-6 p-4 rounded-xl text-sm flex items-start gap-3"
          style={{ backgroundColor: '#FAF7F2', color: '#5A1620' }}
        >
          <FileSpreadsheet className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#C9A961' }} />
          <div>
            <div className="font-semibold mb-0.5">{t('supp.tip_label')}</div>
            <div className="leading-relaxed" dangerouslySetInnerHTML={{ __html: t('supp.tip_text_html') }} />
          </div>
        </div>
      )}
    </Layout>
  );
}
